import { useCallback } from 'react'

import { toast } from '@buffer-mono/popcorn'

import { graphql } from '~publish/graphql'
import { useTypedMutation } from '~publish/hooks/useTypedMutation'

// Each mutation selects only what a resolve changes. The payload's
// conversation is normalised into the cache by id, so anything else selected
// would overwrite the row the list already holds, and the API serves mock
// conversations that do not match that row until DM storage lands.
export const ResolveConversationMutationGQL = graphql(/* GraphQL */ `
  mutation ResolveConversation($input: ResolveConversationInput!) {
    resolveConversation(input: $input) {
      __typename
      ... on ConversationMutationSuccess {
        conversation {
          __typename
          id
          status
          readState {
            unreadCount
            lastReadAt
          }
        }
      }
      ... on VoidMutationError {
        message
      }
    }
  }
`)

export const UnresolveConversationMutationGQL = graphql(/* GraphQL */ `
  mutation UnresolveConversation($input: UnresolveConversationInput!) {
    unresolveConversation(input: $input) {
      __typename
      ... on ConversationMutationSuccess {
        conversation {
          __typename
          id
          status
          readState {
            unreadCount
            lastReadAt
          }
        }
      }
      ... on VoidMutationError {
        message
      }
    }
  }
`)

type UseResolveConversationResult = {
  /** Resolves the conversation; `true` once the API confirms it. */
  resolve: (conversationId: string) => Promise<boolean>
  /** Takes the conversation back out of resolved; `true` once confirmed. */
  unresolve: (conversationId: string) => Promise<boolean>
}

/**
 * Resolve and undo resolve for a DM conversation, shared by the thread
 * header and the conversation card's menu. Both update the row in place
 * through the cache, with no refetch, so a resolved conversation keeps its
 * place in the list until the next load. The outcome is confirmed or
 * reported in a toast, as resolving a comment is.
 */
export function useResolveConversation(): UseResolveConversationResult {
  const [resolveMutation] = useTypedMutation(
    ResolveConversationMutationGQL,
    (data) => data.resolveConversation,
    { successTypename: 'ConversationMutationSuccess' },
  )
  const [unresolveMutation] = useTypedMutation(
    UnresolveConversationMutationGQL,
    (data) => data.unresolveConversation,
    { successTypename: 'ConversationMutationSuccess' },
  )

  const resolve = useCallback(
    async (conversationId: string): Promise<boolean> => {
      const result = await resolveMutation({
        variables: { input: { conversationId } },
      })
      if (result.success) {
        toast.success('Conversation resolved successfully', { duration: 1_000 })
        return true
      }
      toast.error('An error occurred while resolving the conversation', {
        duration: 2_000,
      })
      return false
    },
    [resolveMutation],
  )

  const unresolve = useCallback(
    async (conversationId: string): Promise<boolean> => {
      const result = await unresolveMutation({
        variables: { input: { conversationId } },
      })
      if (result.success) {
        toast.success('Conversation reopened successfully', { duration: 1_000 })
        return true
      }
      toast.error('An error occurred while reopening the conversation', {
        duration: 2_000,
      })
      return false
    },
    [unresolveMutation],
  )

  return { resolve, unresolve }
}
